import { appConfig } from '@common/config';
import { type IPriceItem, type IStock } from '@interface/IItemPrice';
import { ItemStockQueryKey } from '@network/queryKeys';
import { useDataQuery } from '@network/querys';
import { keepPreviousData } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PriceItemStockItem } from './PriceItemStockItem';

export const PriceItemStock = ({
    it,
    isopen,
    qtitySet,
}: {
    it: IPriceItem;
    isopen: boolean;
    qtitySet: React.Dispatch<React.SetStateAction<number>>;
}) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [pagenum, pagenumSet] = useState<number>(0);
    const [stockList, stockListSet] = useState<IStock['contents']>([]);
    const [pagination, paginationSet] = useState<IStock['pagination']>({ page: 0, size: 0, lastPage: 0, total: 0, limit: 10000 });

    useEffect(() => {
        if (isopen && pagenum < 1) pagenumSet(1);
    }, [isopen, pagenum]);

    const { data } = useDataQuery<IStock>(
        ItemStockQueryKey,
        `/${appConfig.service}/v2.0/price/search/item/${it.itemId}/stock`,
        {
            serverId: it.serverId,
            enchantLevel: it.enchantLevel,
            blessedType: searchParams.get('bless') ?? 0,
            page: pagenum,
            size: 5,
        },
        {
            staleTime: 5000,
            placeholderData: keepPreviousData,
            enabled: pagenum > 0,
        }
    );

    useEffect(() => {
        if (data?.contents) {
            stockListSet([...stockList, ...data.contents]);
            paginationSet(data.pagination);
            qtitySet(data.pagination.total);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const onMore = () => {
        pagenumSet((current) => (current < pagination.lastPage ? pagenum + 1 : current));
    };

    if (it.quantity < 1) return null;
    if (stockList && stockList.length < 1) return null;

    return (
        <section className="tradeableitems">
            <header>
                <span className="qty">
                    <Trans>{t('common.itemlistper').replace('{N}', `${pagination.total > 99 ? '99+' : pagination.total}`)}</Trans>
                </span>
            </header>
            <article>
                <ul>
                    {stockList.map((item, idx) => (
                        <PriceItemStockItem key={item.itemId + idx} it={item} />
                    ))}
                </ul>
            </article>
            {pagination.lastPage > pagination.page ? (
                <footer>
                    <button className="btn-more" onClick={onMore}>
                        {t('common.viewmore')}
                        <i className="ico-arrow" />
                    </button>
                </footer>
            ) : null}
        </section>
    );
};
